/**
 * PrPreviewList - the isolated previews branched from one shared environment.
 *
 * It lives inside the environment's card because that is where the question
 * gets asked: a preview is *this* environment's code and a copy of *this*
 * environment's data, and the number a PM wants next to "3 copias · baseline
 * 2026-09-01" is "and two pull requests are running off it right now". A section
 * of its own would have to repeat the environment to say which one it came from.
 *
 * A separate component rather than more of the card for one reason: it owns a
 * request, a mutation and a confirmation, and the card was already the longest
 * file in this folder. Nothing here is reusable by accident — the pull request
 * panel shows the same rows through the same logic module but with different
 * buttons, because there the operation goes through a thread.
 *
 * Two buttons act directly on the server instead of opening a thread, which is
 * the opposite of every other environment operation. That is deliberate and it
 * is not an inconsistency: extending an expiry and reclaiming a review copy need
 * no judgement between a preflight and a merge, there is nothing for an agent to
 * narrate, and routing "this preview is in my way" through a conversation would
 * be ceremony. Building one still opens a thread — that is the half that runs
 * migrations and takes minutes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { EnvironmentId, ProjectId, ThreadId } from "@taskin-code/contracts";

import { EnvironmentsClient, type PrPreviewRow } from "../../auth/EnvironmentsClient";
import { environmentQueryKeys } from "../../lib/environmentQueries";
import { formatRelativeTime } from "../../lib/relativeTime";
import { useUserDirectoryMap } from "../../lib/userDirectoryReactQuery";
import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import {
  describePreviewExpiry,
  describePreviewOperation,
  livePreviewsOf,
  previewStatusPill,
} from "./prPreviews.logic";

/** Why «Alargar» and «Destruir» are inert for somebody who may only look. */
const PREVIEW_DISABLED_REASON = "Necesitas p.pr.preview en este proyecto";

/** What «Alargar» offers. Small steps first, because a preview costs disk. */
const EXTENSIONS: ReadonlyArray<{
  readonly hours: number;
  readonly label: string;
}> = [
  { hours: 24, label: "Un día más" },
  { hours: 72, label: "Tres días más" },
  { hours: 168, label: "Una semana más" },
];

export function PrPreviewList(props: {
  readonly projectId: ProjectId;
  /** Only the previews branched from this environment are this card's business. */
  readonly baseEnvironmentId: EnvironmentId;
  /** `p.pr.preview`: extending an expiry and tearing a preview down. */
  readonly canPreview: boolean;
  /** Go to the thread building or destroying a preview. Same callback as the card's. */
  readonly onOpenThreadPage: (threadId: ThreadId) => void;
}) {
  const queryClient = useQueryClient();
  const directory = useUserDirectoryMap();
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Keyed under `environmentQueryKeys`, so the `pr-preview.*` domain events
  // `__root` already listens for refresh this list with no poll of its own:
  // previews are built and destroyed by an agent or by the expiry sweep, never
  // by whoever is looking at the card.
  const previewsQuery = useQuery({
    queryKey: environmentQueryKeys.prPreviews(props.projectId),
    queryFn: () => EnvironmentsClient.listPrPreviews(props.projectId),
    staleTime: 30_000,
    retry: false,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: environmentQueryKeys.all });

  const extend = useMutation({
    mutationFn: (input: { previewId: string; hours: number }) =>
      EnvironmentsClient.extendPrPreview(input.previewId, input.hours),
    onSuccess: invalidate,
  });

  const destroy = useMutation({
    mutationFn: (previewId: string) => EnvironmentsClient.destroyPrPreview(previewId),
    onSuccess: invalidate,
  });

  const act = async (previewId: string, run: () => Promise<unknown>, failure: string) => {
    setError(null);
    setBusyId(previewId);
    try {
      await run();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : failure);
    } finally {
      setBusyId(null);
    }
  };

  const onDestroy = (preview: PrPreviewRow) => {
    // Said in full rather than "are you sure?": what is lost is whatever was
    // typed into that copy of the data, and the person pressing this may be
    // destroying somebody else's afternoon of clicking through a review.
    if (
      !window.confirm(
        `¿Destruir la preview del PR #${preview.prNumber}? Se borran su base de datos y su ` +
          `worktree, su URL deja de responder y se pierde todo lo que se haya hecho dentro. ` +
          `El entorno compartido no se toca.`,
      )
    ) {
      return;
    }
    void act(preview.id, () => destroy.mutateAsync(preview.id), "No se pudo destruir la preview.");
  };

  // Failure is a line, not an empty list: "no hay previews" for a request that
  // did not answer is the one wrong thing this component could say — somebody
  // would build a second preview of a pull request that already has one.
  if (previewsQuery.isError) {
    return (
      <p className="text-[11px] text-amber-400">
        No se pudo leer la lista de previews:{" "}
        {previewsQuery.error instanceof Error ? previewsQuery.error.message : "error desconocido"}
      </p>
    );
  }

  const previews = livePreviewsOf(previewsQuery.data ?? []).filter(
    (preview) => preview.baseEnvironmentId === props.baseEnvironmentId,
  );
  if (previews.length === 0) return null;

  const nowMs = Date.now();

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border/50 bg-background/40 p-2.5">
      <p className="text-[11px] font-medium text-muted-foreground">
        Previews de PR sobre este entorno
      </p>
      {previews.map((preview) => {
        const pill = previewStatusPill(preview.status);
        const expiry = describePreviewExpiry({
          expiresAt: preview.expiresAt,
          nowMs,
        });
        const operation = preview.activeOperation;
        const busyLine =
          operation === null
            ? null
            : describePreviewOperation({
                operation: operation.operation,
                prNumber: preview.prNumber,
                actorName:
                  operation.userId === null
                    ? null
                    : (directory.get(operation.userId)?.displayName ?? null),
              });
        const requestedBy =
          preview.createdBy === null
            ? null
            : (directory.get(preview.createdBy)?.displayName ?? null);
        // Anything holding the preview's mutex blocks both buttons: the server
        // would answer `busy`, and a button whose only outcome is that message
        // is worse than one that is visibly out of order.
        const acting = busyId === preview.id || operation !== null;

        return (
          <div
            key={preview.id}
            className="flex flex-col gap-1 border-t border-border/40 pt-1.5 first:border-t-0 first:pt-0"
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span className="font-mono font-medium">PR #{preview.prNumber}</span>
              <span
                title={pill.hint}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${pill.className}`}
              >
                <span className={`size-1.5 rounded-full ${pill.dotClassName}`} aria-hidden="true" />
                {pill.label}
              </span>
              {preview.url === null ? (
                <span className="text-muted-foreground">sin URL</span>
              ) : (
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 truncate text-primary underline underline-offset-2"
                >
                  {preview.devHostname}
                </a>
              )}
              {expiry !== null ? (
                <span
                  className={expiry === "caducada" ? "text-amber-400" : "text-muted-foreground/80"}
                >
                  {expiry}
                </span>
              ) : null}
              {requestedBy !== null ? (
                <span className="text-muted-foreground/80">pedida por {requestedBy}</span>
              ) : null}
              <span className="text-muted-foreground/60">
                hace {formatRelativeTime(preview.refreshedAt ?? preview.createdAt)}
              </span>
            </div>

            {/* The files, because "conflict" without them is a dead end: the
                agent cannot resolve them and the author has to know which. */}
            {preview.status === "conflict" && preview.conflicts !== null ? (
              <p className="text-[11px] text-amber-400">
                No mergea: {preview.conflicts.slice(0, 4).join(", ")}
                {preview.conflicts.length > 4 ? ` y ${preview.conflicts.length - 4} más` : ""}
              </p>
            ) : null}
            {preview.failureReason !== null ? (
              <p className="text-[11px] text-red-400">{preview.failureReason}</p>
            ) : null}

            {busyLine !== null ? (
              <p className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                <span>{busyLine}</span>
                {operation?.threadId != null ? (
                  <button
                    type="button"
                    className="text-primary underline underline-offset-2"
                    onClick={() => props.onOpenThreadPage(operation.threadId!)}
                  >
                    Ver el hilo
                  </button>
                ) : null}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-1.5">
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={!props.canPreview || acting}
                      {...(props.canPreview ? {} : { title: PREVIEW_DISABLED_REASON })}
                    />
                  }
                >
                  Alargar ▾
                </MenuTrigger>
                <MenuPopup align="start">
                  {EXTENSIONS.map((option) => (
                    <MenuItem
                      key={option.hours}
                      onClick={() =>
                        void act(
                          preview.id,
                          () =>
                            extend.mutateAsync({
                              previewId: preview.id,
                              hours: option.hours,
                            }),
                          "No se pudo alargar la preview.",
                        )
                      }
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuPopup>
              </Menu>
              <Button
                size="xs"
                variant="outline"
                className="text-red-500 hover:text-red-400"
                disabled={!props.canPreview || acting}
                {...(props.canPreview ? {} : { title: PREVIEW_DISABLED_REASON })}
                onClick={() => onDestroy(preview)}
              >
                {busyId === preview.id ? "Destruyendo…" : "Destruir"}
              </Button>
            </div>
          </div>
        );
      })}
      {error !== null ? <p className="text-[11px] text-red-400">{error}</p> : null}
    </div>
  );
}
